import React, { useEffect, useMemo, useState } from 'react';
import { Button, Chip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  SourceReferenceAllocation,
  SourceSettlementDocumentView,
  SourceSettlementGroupView,
} from 'common/src/api/settlements';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

export type SettlementSourceViewMode = 'document' | 'group';

interface SettlementSourceViewsProps {
  mode: SettlementSourceViewMode;
  documents: SourceSettlementDocumentView[];
  filteredSettlementIds: Set<number>;
  settlements: Settlement[];
  searchTerm: string;
  onOpenSettlement: (settlementId: number) => void;
  onOpenPdf: (
    bucketId: string,
    storagePath: string,
    fileName: string,
    pageNumber: number
  ) => void;
}

type CurrencyTotals = {
  readSales: number;
  readCosts: number;
  readNet: number;
  allocatedSales: number;
  allocatedCosts: number;
  allocatedNet: number;
};

type DisplayedSourceGroup = SourceSettlementGroupView & {
  isPartial?: boolean;
};

type DisplayedSourceDocument = Omit<SourceSettlementDocumentView, 'groups'> & {
  groups: DisplayedSourceGroup[];
  isPartial?: boolean;
};

const PAGE_SIZE = 50;
const QUANTITY_TOLERANCE = 0.001;
const MONEY_TOLERANCE = 0.01;

const toNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const hasNumericValue = (record: Record<string, unknown>, key: string) =>
  record[key] !== null &&
  record[key] !== undefined &&
  record[key] !== '' &&
  Number.isFinite(Number(record[key]));

const formatNumber = (value: number, decimals = 0) =>
  new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: decimals,
  }).format(value || 0);

const formatMoney = (value: number, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(value || 0);
  } catch {
    return `${currency || 'USD'} ${formatNumber(value, 2)}`;
  }
};

const normalizeSearch = (value: unknown) =>
  String(value || '')
    .trim()
    .toLocaleLowerCase('es');

const getGroupCurrency = (group: SourceSettlementGroupView) =>
  String(
    group.extractedHeader.currency || group.moneyTotals[0]?.currency || 'USD'
  );

const getGroupBalance = (group: SourceSettlementGroupView) => {
  const comparable: boolean[] = [];
  const read = group.extractedTotals;
  const currency = getGroupCurrency(group);
  const allocated = group.moneyTotals.find(
    (total) => normalizeSearch(total.currency) === normalizeSearch(currency)
  );

  if (hasNumericValue(read, 'cases')) {
    comparable.push(
      Math.abs(toNumber(read.cases) - toNumber(group.quantity)) <=
        QUANTITY_TOLERANCE
    );
  }
  if (allocated && hasNumericValue(read, 'grossSales')) {
    comparable.push(
      Math.abs(toNumber(read.grossSales) - allocated.sales) <= MONEY_TOLERANCE
    );
  } else if (hasNumericValue(read, 'grossSales')) {
    comparable.push(false);
  }
  if (allocated && hasNumericValue(read, 'totalExpenses')) {
    comparable.push(
      Math.abs(toNumber(read.totalExpenses) - allocated.totalCosts) <=
        MONEY_TOLERANCE
    );
  } else if (hasNumericValue(read, 'totalExpenses')) {
    comparable.push(false);
  }

  return comparable.length > 0 ? comparable.every(Boolean) : null;
};

const filterSourceGroup = (
  group: SourceSettlementGroupView,
  includeReference: (reference: SourceReferenceAllocation) => boolean
): DisplayedSourceGroup | null => {
  const references = group.references.filter(includeReference);
  if (references.length === 0) return null;

  const moneyByCurrency = new Map<
    string,
    SourceSettlementGroupView['moneyTotals'][number]
  >();
  references.forEach((reference) => {
    const current = moneyByCurrency.get(reference.currency) || {
      currency: reference.currency,
      sales: 0,
      tax: 0,
      commission: 0,
      freight: 0,
      repacking: 0,
      otherCosts: 0,
      totalCosts: 0,
      net: 0,
    };
    current.sales += reference.sales;
    current.tax += reference.tax;
    current.commission += reference.commission;
    current.freight += reference.freight;
    current.repacking += reference.repacking;
    current.otherCosts += reference.otherCosts;
    current.totalCosts += reference.totalCosts;
    current.net += reference.net;
    moneyByCurrency.set(reference.currency, current);
  });

  return {
    ...group,
    references,
    quantity: references.reduce(
      (total, reference) => total + reference.quantity,
      0
    ),
    kilos: references.reduce((total, reference) => total + reference.kilos, 0),
    settlementCount: new Set(
      references.map((reference) => reference.settlementId)
    ).size,
    moneyTotals: Array.from(moneyByCurrency.values()).sort((a, b) =>
      a.currency.localeCompare(b.currency, 'es')
    ),
    isPartial: references.length !== group.references.length,
  };
};

const aggregateDocumentCurrencies = (
  document: SourceSettlementDocumentView
) => {
  const totals = new Map<string, CurrencyTotals>();
  document.groups.forEach((group) => {
    const readCurrency = getGroupCurrency(group);
    const currentRead = totals.get(readCurrency) || {
      readSales: 0,
      readCosts: 0,
      readNet: 0,
      allocatedSales: 0,
      allocatedCosts: 0,
      allocatedNet: 0,
    };
    currentRead.readSales += toNumber(group.extractedTotals.grossSales);
    currentRead.readCosts += toNumber(group.extractedTotals.totalExpenses);
    currentRead.readNet += toNumber(group.extractedTotals.netProceeds);
    totals.set(readCurrency, currentRead);

    group.moneyTotals.forEach((money) => {
      const current = totals.get(money.currency) || {
        readSales: 0,
        readCosts: 0,
        readNet: 0,
        allocatedSales: 0,
        allocatedCosts: 0,
        allocatedNet: 0,
      };
      current.allocatedSales += money.sales;
      current.allocatedCosts += money.totalCosts;
      current.allocatedNet += money.net;
      totals.set(money.currency, current);
    });
  });
  return Array.from(totals.entries()).sort(([a], [b]) =>
    a.localeCompare(b, 'es')
  );
};

const BalanceChip: React.FC<{ balance: boolean | null }> = ({ balance }) => {
  const { t } = useTranslation('settlements');
  if (balance === null) {
    return (
      <Chip size='sm' variant='flat'>
        {t('Sin total comparable')}
      </Chip>
    );
  }
  return (
    <Chip
      size='sm'
      color={balance ? 'success' : 'warning'}
      variant='flat'
      startContent={balance ? <Icon icon='tabler:check' /> : undefined}
    >
      {balance ? t('Conciliado') : t('Con diferencias')}
    </Chip>
  );
};

const ReferenceRows: React.FC<{
  references: SourceReferenceAllocation[];
  onOpenSettlement: (settlementId: number) => void;
}> = ({ references, onOpenSettlement }) => {
  const { t } = useTranslation('settlements');
  return (
    <div className='overflow-x-auto rounded-md border border-default-200'>
      <table className='w-full min-w-[920px] text-sm'>
        <thead className='bg-default-100 text-xs uppercase text-default-500'>
          <tr>
            <th className='px-3 py-2 text-left'>{t('Referencia')}</th>
            <th className='px-3 py-2 text-left'>{t('Nave')}</th>
            <th className='px-3 py-2 text-left'>{t('Contenedor')}</th>
            <th className='px-3 py-2 text-right'>{t('Bultos')}</th>
            <th className='px-3 py-2 text-right'>{t('Kilos')}</th>
            <th className='px-3 py-2 text-right'>{t('Venta')}</th>
            <th className='px-3 py-2 text-right'>{t('Costos')}</th>
            <th className='px-3 py-2 text-right'>{t('Neto')}</th>
            <th className='px-3 py-2 text-center'>{t('Acciones')}</th>
          </tr>
        </thead>
        <tbody>
          {references.map((reference) => (
            <tr
              key={`${reference.settlementId}-${reference.referenceNumber}`}
              className='border-t border-default-100'
            >
              <td className='px-3 py-2 font-semibold'>
                {reference.referenceNumber || `ID ${reference.settlementId}`}
              </td>
              <td className='px-3 py-2'>{reference.shipName || '-'}</td>
              <td className='px-3 py-2'>{reference.containerNumber || '-'}</td>
              <td className='px-3 py-2 text-right'>
                {formatNumber(reference.quantity)}
              </td>
              <td className='px-3 py-2 text-right'>
                {formatNumber(reference.kilos)}
              </td>
              <td className='px-3 py-2 text-right'>
                {formatMoney(reference.sales, reference.currency)}
              </td>
              <td className='px-3 py-2 text-right'>
                {formatMoney(reference.totalCosts, reference.currency)}
              </td>
              <td className='px-3 py-2 text-right'>
                {formatMoney(reference.net, reference.currency)}
              </td>
              <td className='px-3 py-2 text-center'>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  aria-label={t('Abrir liquidación')}
                  onPress={() => onOpenSettlement(reference.settlementId)}
                >
                  <Icon icon='tabler:eye' />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GroupCard: React.FC<{
  group: DisplayedSourceGroup;
  document: DisplayedSourceDocument;
  onOpenSettlement: (settlementId: number) => void;
  onOpenPdf: SettlementSourceViewsProps['onOpenPdf'];
}> = ({ group, document, onOpenSettlement, onOpenPdf }) => {
  const { t } = useTranslation('settlements');
  const readCases = hasNumericValue(group.extractedTotals, 'cases')
    ? toNumber(group.extractedTotals.cases)
    : null;
  const balance = group.isPartial ? null : getGroupBalance(group);
  const readCurrency = getGroupCurrency(group);
  const scopeLabels: Record<string, string> = {
    shipNames: t('Nave'),
    referenceNumbers: t('Referencias'),
    containerNumbers: t('Contenedores'),
    varieties: t('Variedad'),
    calibers: t('Calibre'),
    colors: t('Color'),
    categories: t('Categoría'),
    labels: t('Etiqueta'),
    formats: t('Formato'),
    lots: t('Lote'),
    pallets: t('Pallet'),
  };
  const scopeEntries = Object.keys(scopeLabels)
    .map((field) => {
      const values = group.scopeFilter[field];
      const displayValues = Array.isArray(values)
        ? values.filter(
            (value) => typeof value === 'string' || typeof value === 'number'
          )
        : [];
      return [field, displayValues] as const;
    })
    .filter(([, values]) => values.length > 0);

  return (
    <details className='group overflow-hidden rounded-lg border border-default-200 bg-content1'>
      <summary className='cursor-pointer list-none p-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='flex min-w-0 items-start gap-2'>
            <Icon icon='tabler:chevron-right' className='mt-1 shrink-0 text-default-400 transition-transform group-open:rotate-90' />
            <div className='min-w-0'>
              <p className='truncate font-semibold'>{group.name}</p>
              <p className='text-xs text-default-500'>
                {document.fileName} · {t('Páginas')}{' '}
                {group.pages.join(', ') || '-'}
              </p>
            </div>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Chip size='sm' variant='flat'>
              {group.references.length} {t('referencias')}
            </Chip>
            <Chip size='sm' variant='flat'>
              {readCases === null ? 'N/A' : formatNumber(readCases)} /{' '}
              {formatNumber(group.quantity)} {t('bultos')}
            </Chip>
            {group.isPartial ? (
              <Chip size='sm' variant='flat'>
                {t('Vista filtrada')}
              </Chip>
            ) : (
              <BalanceChip balance={balance} />
            )}
            <span
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:file-type-pdf' />}
                onPress={() =>
                  onOpenPdf(
                    document.bucketId,
                    document.storagePath,
                    document.fileName,
                    group.pages[0] || 1
                  )
                }
              >
                {t('Abrir PDF')}
              </Button>
            </span>
          </div>
        </div>
      </summary>
      <div className='space-y-3 border-t border-default-200 p-4'>
        {scopeEntries.length > 0 && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-xs font-semibold uppercase text-default-400'>
              {t('Filtros usados')}
            </span>
            {scopeEntries.map(([field, values]) => (
              <Chip key={field} size='sm' variant='bordered'>
                {scopeLabels[field] || field}:{' '}
                {(values as unknown[]).map(String).join(', ')}
              </Chip>
            ))}
          </div>
        )}
        <div className='grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4'>
          <div className='rounded-md bg-default-50 p-3 dark:bg-default-100'>
            <p className='text-xs text-default-500'>{t('Leído del PDF')}</p>
            <p className='font-semibold'>
              {readCases === null ? 'N/A' : formatNumber(readCases)}{' '}
              {t('bultos')}
            </p>
          </div>
          <div className='rounded-md bg-default-50 p-3 dark:bg-default-100'>
            <p className='text-xs text-default-500'>{t('Creado')}</p>
            <p className='font-semibold'>
              {formatNumber(group.quantity)} {t('bultos')} ·{' '}
              {formatNumber(group.kilos)} kg
            </p>
          </div>
          <div className='rounded-md bg-default-50 p-3 dark:bg-default-100'>
            <p className='text-xs text-default-500'>
              {t('Leído del PDF')}
              {group.isPartial ? ` · ${t('grupo completo')}` : ''} ·{' '}
              {readCurrency}
            </p>
            <p className='font-semibold'>
              {hasNumericValue(group.extractedTotals, 'grossSales')
                ? formatMoney(
                    toNumber(group.extractedTotals.grossSales),
                    readCurrency
                  )
                : 'N/A'}{' '}
              {t('venta')}
            </p>
            <p className='text-xs text-default-500'>
              {hasNumericValue(group.extractedTotals, 'totalExpenses')
                ? formatMoney(
                    toNumber(group.extractedTotals.totalExpenses),
                    readCurrency
                  )
                : 'N/A'}{' '}
              {t('costos')} ·{' '}
              {hasNumericValue(group.extractedTotals, 'netProceeds')
                ? formatMoney(
                    toNumber(group.extractedTotals.netProceeds),
                    readCurrency
                  )
                : 'N/A'}{' '}
              {t('neto')}
            </p>
          </div>
          {group.moneyTotals.map((money) => (
            <div
              key={money.currency}
              className='rounded-md bg-default-50 p-3 dark:bg-default-100'
            >
              <p className='text-xs text-default-500'>
                {t('Asignado')} · {money.currency}
              </p>
              <p className='font-semibold'>
                {formatMoney(money.sales, money.currency)} {t('venta')}
              </p>
              <p className='text-xs text-default-500'>
                {formatMoney(money.totalCosts, money.currency)} {t('costos')} ·{' '}
                {formatMoney(money.net, money.currency)} {t('neto')}
              </p>
            </div>
          ))}
        </div>
        <ReferenceRows
          references={group.references}
          onOpenSettlement={onOpenSettlement}
        />
      </div>
    </details>
  );
};

const DocumentCard: React.FC<{
  document: DisplayedSourceDocument;
  onOpenSettlement: (settlementId: number) => void;
  onOpenPdf: SettlementSourceViewsProps['onOpenPdf'];
}> = ({ document, onOpenSettlement, onOpenPdf }) => {
  const { t } = useTranslation('settlements');
  const referenceCount = new Set(
    document.groups.flatMap((group) =>
      group.references.map((reference) => reference.settlementId)
    )
  ).size;
  const readCases = document.groups.reduce(
    (total, group) => total + toNumber(group.extractedTotals.cases),
    0
  );
  const hasReadCases = document.groups.some((group) =>
    hasNumericValue(group.extractedTotals, 'cases')
  );
  const allocatedCases = document.groups.reduce(
    (total, group) => total + toNumber(group.quantity),
    0
  );
  const balances = document.groups.map(getGroupBalance);
  const documentBalance = document.isPartial
    ? null
    : balances.some((balance) => balance !== null)
      ? balances.every((balance) => balance === null || balance)
      : null;
  const currencyTotals = aggregateDocumentCurrencies(document);

  return (
    <details className='group overflow-hidden rounded-lg border border-default-200 bg-content1 shadow-sm'>
      <summary className='cursor-pointer list-none p-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='flex min-w-0 items-start gap-2'>
            <Icon icon='tabler:chevron-right' className='mt-1 shrink-0 text-default-400 transition-transform group-open:rotate-90' />
            <Icon icon='tabler:folder-open' className='mt-0.5 shrink-0 text-primary' />
            <div className='min-w-0'>
              <p className='truncate font-semibold'>{document.fileName}</p>
              <p className='text-xs text-default-500'>
                {document.receiverName || '-'} ·{' '}
                {new Date(document.createdAt).toLocaleDateString('es-CL')}
              </p>
            </div>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Chip size='sm' variant='flat'>
              {document.groups.length} {t('grupos')}
            </Chip>
            <Chip size='sm' variant='flat'>
              {referenceCount} {t('referencias')}
            </Chip>
            <Chip size='sm' variant='flat'>
              {hasReadCases ? formatNumber(readCases) : 'N/A'} /{' '}
              {formatNumber(allocatedCases)} {t('bultos')}
            </Chip>
            <Chip size='sm' variant='flat'>
              {t(document.status === 'applied' ? 'Aplicado' : document.status)}
            </Chip>
            {document.isPartial ? (
              <Chip size='sm' variant='flat'>
                {t('Vista filtrada')}
              </Chip>
            ) : (
              <BalanceChip balance={documentBalance} />
            )}
            <span
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <Button
                size='sm'
                variant='flat'
                startContent={<Icon icon='tabler:file-type-pdf' />}
                onPress={() =>
                  onOpenPdf(
                    document.bucketId,
                    document.storagePath,
                    document.fileName,
                    1
                  )
                }
              >
                {t('Abrir PDF')}
              </Button>
            </span>
          </div>
        </div>
      </summary>
      <div className='space-y-4 border-t border-default-200 p-4'>
        <div className='grid grid-cols-1 gap-2 lg:grid-cols-2'>
          {currencyTotals.map(([currency, totals]) => (
            <div
              key={currency}
              className='grid grid-cols-2 gap-3 rounded-md border border-default-200 p-3 text-sm'
            >
              <div>
                <p className='text-xs font-semibold uppercase text-default-400'>
                  {t('Leído del PDF')}
                  {document.isPartial ? ` · ${t('grupo completo')}` : ''} ·{' '}
                  {currency}
                </p>
                <p>
                  {formatMoney(totals.readSales, currency)} {t('venta')}
                </p>
                <p className='text-default-500'>
                  {formatMoney(totals.readCosts, currency)} {t('costos')} ·{' '}
                  {formatMoney(totals.readNet, currency)} {t('neto')}
                </p>
              </div>
              <div>
                <p className='text-xs font-semibold uppercase text-default-400'>
                  {t('Creado')} · {currency}
                </p>
                <p>
                  {formatMoney(totals.allocatedSales, currency)} {t('venta')}
                </p>
                <p className='text-default-500'>
                  {formatMoney(totals.allocatedCosts, currency)} {t('costos')} ·{' '}
                  {formatMoney(totals.allocatedNet, currency)} {t('neto')}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className='space-y-3'>
          {document.groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              document={document}
              onOpenSettlement={onOpenSettlement}
              onOpenPdf={onOpenPdf}
            />
          ))}
        </div>
      </div>
    </details>
  );
};

const LegacyBucket: React.FC<{
  settlements: Settlement[];
  onOpenSettlement: (settlementId: number) => void;
}> = ({ settlements, onOpenSettlement }) => {
  const { t } = useTranslation('settlements');
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(settlements.length / PAGE_SIZE));
  const visibleSettlements = settlements.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => setPage(1), [settlements]);

  return (
    <details className='group overflow-hidden rounded-lg border border-warning-200 bg-warning-50/40 dark:bg-warning-100/10'>
      <summary className='cursor-pointer list-none p-4'>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            <Icon icon='tabler:chevron-right' className='text-warning-500 transition-transform group-open:rotate-90' />
            <Icon icon='tabler:link' className='text-warning-500' />
            <div>
              <p className='font-semibold'>{t('Sin documento de origen')}</p>
              <p className='text-xs text-default-500'>
                {t('Liquidaciones creadas sin trazabilidad multi-embarque')}
              </p>
            </div>
          </div>
          <Chip size='sm' color='warning' variant='flat'>
            {settlements.length} {t('liquidaciones')}
          </Chip>
        </div>
      </summary>
      <div className='border-t border-warning-200 p-4'>
        <div className='overflow-x-auto rounded-md border border-default-200 bg-content1'>
          <table className='w-full min-w-[700px] text-sm'>
            <thead className='bg-default-100 text-xs uppercase text-default-500'>
              <tr>
                <th className='px-3 py-2 text-left'>{t('Referencia')}</th>
                <th className='px-3 py-2 text-left'>{t('Cliente')}</th>
                <th className='px-3 py-2 text-left'>{t('Nave')}</th>
                <th className='px-3 py-2 text-right'>{t('Bultos')}</th>
                <th className='px-3 py-2 text-right'>{t('Kilos')}</th>
                <th className='px-3 py-2 text-center'>{t('Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleSettlements.map((settlement) => (
                <tr key={settlement.id} className='border-t border-default-100'>
                  <td className='px-3 py-2 font-semibold'>
                    {settlement.reference_number || `ID ${settlement.id}`}
                  </td>
                  <td className='px-3 py-2'>{settlement.receiver || '-'}</td>
                  <td className='px-3 py-2'>
                    {settlement.ship_trip?.ship?.name || '-'}
                  </td>
                  <td className='px-3 py-2 text-right'>
                    {formatNumber(
                      settlement.settlement_details.reduce(
                        (total, detail) => total + (detail.quantity || 0),
                        0
                      )
                    )}
                  </td>
                  <td className='px-3 py-2 text-right'>
                    {formatNumber(
                      settlement.settlement_details.reduce(
                        (total, detail) => total + (detail.kilos || 0),
                        0
                      )
                    )}
                  </td>
                  <td className='px-3 py-2 text-center'>
                    <Button
                      isIconOnly
                      size='sm'
                      variant='light'
                      onPress={() => onOpenSettlement(settlement.id)}
                    >
                      <Icon icon='tabler:eye' />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className='mt-3 flex items-center justify-between gap-2'>
            <p className='text-xs text-default-500'>
              {t('Página')} {page} {t('de')} {totalPages}
            </p>
            <div className='flex gap-2'>
              <Button
                size='sm'
                variant='light'
                isDisabled={page <= 1}
                onPress={() => setPage((current) => Math.max(1, current - 1))}
              >
                {t('Anterior')}
              </Button>
              <Button
                size='sm'
                variant='light'
                isDisabled={page >= totalPages}
                onPress={() =>
                  setPage((current) => Math.min(totalPages, current + 1))
                }
              >
                {t('Siguiente')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </details>
  );
};

const SettlementSourceViews: React.FC<SettlementSourceViewsProps> = ({
  mode,
  documents,
  filteredSettlementIds,
  settlements,
  searchTerm,
  onOpenSettlement,
  onOpenPdf,
}) => {
  const { t } = useTranslation('settlements');
  const [page, setPage] = useState(1);
  const normalizedSearch = normalizeSearch(searchTerm);

  const traceableSettlementIds = useMemo(
    () =>
      new Set(
        documents.flatMap((document) =>
          document.groups.flatMap((group) =>
            group.references.map((reference) => reference.settlementId)
          )
        )
      ),
    [documents]
  );

  const legacySettlements = useMemo(
    () =>
      settlements.filter(
        (settlement) =>
          filteredSettlementIds.has(settlement.id) &&
          !traceableSettlementIds.has(settlement.id) &&
          (!normalizedSearch ||
            normalizeSearch(settlement.reference_number).includes(
              normalizedSearch
            ) ||
            normalizeSearch(t('Sin documento de origen')).includes(
              normalizedSearch
            ))
      ),
    [
      filteredSettlementIds,
      normalizedSearch,
      settlements,
      t,
      traceableSettlementIds,
    ]
  );

  const visibleDocuments = useMemo(
    () =>
      documents.flatMap((document): DisplayedSourceDocument[] => {
        const documentMatches =
          Boolean(normalizedSearch) &&
          normalizeSearch(document.fileName).includes(normalizedSearch);
        const groups = document.groups.flatMap(
          (group): DisplayedSourceGroup[] => {
            const groupMatches =
              documentMatches ||
              (Boolean(normalizedSearch) &&
                normalizeSearch(group.name).includes(normalizedSearch));
            const filteredGroup = filterSourceGroup(
              group,
              (reference) =>
                filteredSettlementIds.has(reference.settlementId) &&
                (!normalizedSearch ||
                  groupMatches ||
                  normalizeSearch(reference.referenceNumber).includes(
                    normalizedSearch
                  ))
            );
            return filteredGroup ? [filteredGroup] : [];
          }
        );
        if (groups.length === 0) return [];

        const isPartial =
          groups.length !== document.groups.length ||
          groups.some((group) => group.isPartial);
        return [{ ...document, groups, isPartial }];
      }),
    [documents, filteredSettlementIds, normalizedSearch]
  );

  const visibleGroups = useMemo(
    () =>
      documents.flatMap((document) =>
        document.groups.flatMap((group) => {
          const scopeMatches =
            !normalizedSearch ||
            normalizeSearch(document.fileName).includes(normalizedSearch) ||
            normalizeSearch(group.name).includes(normalizedSearch);
          const filteredGroup = filterSourceGroup(
            group,
            (reference) =>
              filteredSettlementIds.has(reference.settlementId) &&
              (scopeMatches ||
                normalizeSearch(reference.referenceNumber).includes(
                  normalizedSearch
                ))
          );
          if (!filteredGroup) return [];
          const displayedDocument: DisplayedSourceDocument = {
            ...document,
            groups: [filteredGroup],
            isPartial:
              document.groups.length !== 1 || Boolean(filteredGroup.isPartial),
          };
          return [{ group: filteredGroup, document: displayedDocument }];
        })
      ),
    [documents, filteredSettlementIds, normalizedSearch]
  );

  const entityCount =
    (mode === 'document' ? visibleDocuments.length : visibleGroups.length) +
    (legacySettlements.length ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(entityCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;

  useEffect(() => setPage(1), [mode, normalizedSearch, filteredSettlementIds]);

  const legacyPosition =
    mode === 'document' ? visibleDocuments.length : visibleGroups.length;
  const showLegacy =
    legacySettlements.length > 0 &&
    legacyPosition >= startIndex &&
    legacyPosition < endIndex;

  if (entityCount === 0) {
    return (
      <div className='rounded-lg border border-dashed border-default-300 p-12 text-center text-default-500'>
        {t('No se encontraron documentos o grupos para estos filtros')}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-3'>
        {mode === 'document'
          ? visibleDocuments
              .slice(startIndex, endIndex)
              .map((document) => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  onOpenSettlement={onOpenSettlement}
                  onOpenPdf={onOpenPdf}
                />
              ))
          : visibleGroups
              .slice(startIndex, endIndex)
              .map(({ group, document }) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  document={document}
                  onOpenSettlement={onOpenSettlement}
                  onOpenPdf={onOpenPdf}
                />
              ))}
        {showLegacy && (
          <LegacyBucket
            settlements={legacySettlements}
            onOpenSettlement={onOpenSettlement}
          />
        )}
      </div>
      {totalPages > 1 && (
        <div className='flex items-center justify-between gap-3'>
          <p className='text-sm text-default-500'>
            {t('Página')} {page} {t('de')} {totalPages}
          </p>
          <div className='flex gap-2'>
            <Button
              size='sm'
              variant='flat'
              isDisabled={page <= 1}
              onPress={() => setPage((current) => Math.max(1, current - 1))}
            >
              {t('Anterior')}
            </Button>
            <Button
              size='sm'
              variant='flat'
              isDisabled={page >= totalPages}
              onPress={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
            >
              {t('Siguiente')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementSourceViews;
